import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  SubscriptionResponse,
  AccessInfo,
  TrialInfo,
  SubscriptionPlan,
  SubscriptionFeature,
  hasSubscription,
} from '../types/subscription';
import * as subscriptionService from '../services/subscriptionService';
import { useFieldAuth } from './FieldContext';
import { useRefereeAuth } from './RefereeContext';

interface SubscriptionContextType {
  // Состояние подписки
  subscription: SubscriptionResponse | null;
  accessInfo: AccessInfo | null;
  trialInfo: TrialInfo | null;
  plans: SubscriptionPlan[] | null;

  // Состояние загрузки
  loading: boolean;
  error: string | null;

  // Проверка доступа к фичам
  hasFeature: (feature: SubscriptionFeature) => boolean;
  isTrialing: boolean;
  trialDaysRemaining: number | null;
  hasActiveSubscription: boolean;

  // Действия
  refreshSubscription: () => Promise<void>;
  loadPlans: () => Promise<void>;
  redirectToCheckout: (planId: string, billingInterval: 'monthly' | 'yearly') => Promise<void>;
  redirectToPortal: (returnUrl?: string) => Promise<void>;
  cancelSubscription: (atPeriodEnd?: boolean) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { field, isFieldAuthenticated } = useFieldAuth();
  const { referee, isRefereeAuthenticated } = useRefereeAuth();

  // Определяем, авторизован ли пользователь (поле или судья)
  const isAuthenticated = isFieldAuthenticated || isRefereeAuthenticated;

  // Загрузка подписки и информации о доступе
  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setAccessInfo(null);
      setTrialInfo(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [subscriptionData, accessData, trialData] = await Promise.all([
        subscriptionService.getMySubscription(),
        subscriptionService.getAccessInfo(),
        subscriptionService.getTrialInfo(),
      ]);

      setSubscription(subscriptionData);
      setAccessInfo(accessData);
      setTrialInfo(trialData);
    } catch (err: any) {
      console.error('Error loading subscription data:', err);
      setError(err.message || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Загрузка доступных планов
  const loadPlans = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const plansData = await subscriptionService.getAvailablePlans();
      setPlans(plansData);
    } catch (err: any) {
      console.error('Error loading plans:', err);
    }
  }, [isAuthenticated]);

  // Редирект на Stripe Checkout
  const redirectToCheckout = useCallback(async (planId: string, billingInterval: 'monthly' | 'yearly') => {
    try {
      await subscriptionService.redirectToCheckout(planId, billingInterval);
    } catch (err: any) {
      setError(err.message || 'Failed to redirect to checkout');
      throw err;
    }
  }, []);

  // Редирект на Stripe Customer Portal
  const redirectToPortal = useCallback(async (returnUrl?: string) => {
    try {
      await subscriptionService.redirectToPortal(returnUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to redirect to portal');
      throw err;
    }
  }, []);

  // Отмена подписки
  const cancelSubscription = useCallback(async (atPeriodEnd: boolean = true) => {
    try {
      await subscriptionService.cancelSubscription({ atPeriodEnd });
      // Обновляем данные после отмены
      await refreshSubscription();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
      throw err;
    }
  }, [refreshSubscription]);

  // Проверка доступа к фиче
  const hasFeatureCheck = useCallback((feature: SubscriptionFeature): boolean => {
    if (!accessInfo) return false;
    return accessInfo.activeFeatures.includes(feature);
  }, [accessInfo]);

  // Автоматическая загрузка при авторизации
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isAuthenticated) {
        // Очищаем данные при логауте
        setSubscription(null);
        setAccessInfo(null);
        setTrialInfo(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [subscriptionData, accessData, trialData] = await Promise.all([
          subscriptionService.getMySubscription(),
          subscriptionService.getAccessInfo(),
          subscriptionService.getTrialInfo(),
        ]);

        if (isMounted) {
          setSubscription(subscriptionData);
          setAccessInfo(accessData);
          setTrialInfo(trialData);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Error loading subscription data:', err);
          setError(err.message || 'Failed to load subscription');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, field?.id, referee?.id]);

  // Вычисляемые свойства
  const isTrialing = accessInfo?.isTrialing ?? false;
  const trialDaysRemaining = accessInfo?.trialDaysRemaining ?? null;
  const hasActiveSubscription = accessInfo?.hasAccess ?? false;

  const value: SubscriptionContextType = {
    subscription,
    accessInfo,
    trialInfo,
    plans,
    loading,
    error,
    hasFeature: hasFeatureCheck,
    isTrialing,
    trialDaysRemaining,
    hasActiveSubscription,
    refreshSubscription,
    loadPlans,
    redirectToCheckout,
    redirectToPortal,
    cancelSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
